<template>
  <div class="status-mapping-tab">
    <div class="status-mapping-container">
      <div class="section-header">
        <h3>Status Mapping</h3>
        <p class="section-description">
          Map ServiceM8 job statuses to {{ brand.agencyName }} pipeline stages and/or opportunity statuses.
          When a job status changes (e.g. Completed, Quote Accepted, Unsuccessful), the linked
          opportunity will move to the mapped stage and/or status — and vice versa during sync.
        </p>
      </div>

      <div v-if="loading" class="loading-state">
        <p>Loading statuses and pipelines...</p>
      </div>

      <div v-else-if="error" class="error-state">
        <p>{{ error }}</p>
        <button @click="loadData" class="btn-retry">Retry</button>
      </div>

      <div v-else class="mapping-table-container">
        <div class="fallback-card">
          <h4>Fallback / default stage</h4>
          <p class="fallback-copy">
            Used in the ServiceM8 job iframe when the job has no queue mapping and no matching status mapping.
          </p>
          <div class="fallback-row">
            <label>{{ brand.agencyName }} Pipeline Stage</label>
            <select
              :value="getMapping(FALLBACK_KEY).pipelineStageId || ''"
              @change="handleStageChange(FALLBACK_KEY, $event.target.value)"
              class="pipeline-select"
            >
              <option value="">— None —</option>
              <optgroup
                v-for="pipeline in pipelines"
                :key="pipeline.id"
                :label="pipeline.name"
              >
                <option
                  v-for="stage in pipeline.stages || []"
                  :key="stage.id"
                  :value="stage.id"
                >
                  {{ stage.name }}
                </option>
              </optgroup>
            </select>
            <label>{{ brand.agencyName }} Opportunity Status</label>
            <select
              :value="getMapping(FALLBACK_KEY).opportunityStatus || ''"
              @change="handleOppStatusChange(FALLBACK_KEY, $event.target.value)"
              class="status-select"
            >
              <option value="">— None —</option>
              <option value="open">Open</option>
              <option value="won">Won</option>
              <option value="lost">Lost</option>
              <option value="abandoned">Abandoned</option>
            </select>
          </div>
        </div>

        <div class="table-header">
          <div class="table-header-left">
            <h4>Job Status Mappings</h4>
            <span class="status-count">{{ displayStatuses.length }} status(es)</span>
          </div>
          <div class="table-header-right">
            <button @click="loadData" class="btn-refresh" :disabled="loading">
              {{ loading ? 'Loading...' : 'Refresh' }}
            </button>
          </div>
        </div>

        <table class="mapping-table">
          <thead>
            <tr>
              <th>ServiceM8 Job Status</th>
              <th>Sync Direction</th>
              <th>{{ brand.agencyName }} Pipeline Stage</th>
              <th>{{ brand.agencyName }} Opportunity Status</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="status in displayStatuses" :key="status">
              <td class="status-cell">
                <span class="status-name">{{ status }}</span>
              </td>
              <td class="sync-direction-cell">
                <span class="sync-arrow" title="Sync flows both ways">↔</span>
              </td>
              <td class="pipeline-cell">
                <select
                  :value="getMapping(status).pipelineStageId || ''"
                  @change="handleStageChange(status, $event.target.value)"
                  class="pipeline-select"
                >
                  <option value="">— None —</option>
                  <optgroup
                    v-for="pipeline in pipelines"
                    :key="pipeline.id"
                    :label="pipeline.name"
                  >
                    <option
                      v-for="stage in pipeline.stages || []"
                      :key="stage.id"
                      :value="stage.id"
                    >
                      {{ stage.name }}
                    </option>
                  </optgroup>
                </select>
              </td>
              <td class="opp-status-cell">
                <select
                  :value="getMapping(status).opportunityStatus || ''"
                  @change="handleOppStatusChange(status, $event.target.value)"
                  class="status-select"
                >
                  <option value="">— None —</option>
                  <option value="open">Open</option>
                  <option value="won">Won</option>
                  <option value="lost">Lost</option>
                  <option value="abandoned">Abandoned</option>
                </select>
              </td>
            </tr>
          </tbody>
        </table>

        <p class="help-note">
          You can map a stage only, a status only, or both. At least one target is required for sync to apply.
        </p>
      </div>
    </div>
  </div>
</template>

<script>
const FALLBACK_KEY = '__fallback__';
const DEFAULT_JOB_STATUSES = [
  'Quote',
  'Work Order',
  'Quote Accepted',
  'Completed',
  'Unsuccessful',
];

import { brandMixin } from '../brandMixin'

export default {
  name: 'StatusMappingTab',
  mixins: [brandMixin],
  data() {
    return {
      FALLBACK_KEY,
      pipelines: [],
      statusMappings: {},
      displayStatuses: DEFAULT_JOB_STATUSES,
      loading: true,
      error: null,
    };
  },
  async mounted() {
    await this.loadData();
  },
  methods: {
    getMapping(status) {
      return this.statusMappings[status] || {};
    },
    async loadData() {
      this.loading = true;
      this.error = null;
      try {
        const ssoToken = await this.getSSOToken();
        const headers = { 'x-sso-token': ssoToken };

        const [mappingRes, pipelineRes] = await Promise.all([
          fetch('/status-mappings', { headers }),
          fetch('/servicem8/pipelines', { headers }),
        ]);

        if (!mappingRes.ok) throw new Error('Failed to load status mappings');
        if (!pipelineRes.ok) throw new Error('Failed to load pipelines');

        const mappingData = await mappingRes.json();
        this.statusMappings = mappingData.mappings || {};
        const apiStatuses = (mappingData.jobStatuses || []).filter(
          (s) => s !== 'Unsucessful'
        );
        this.displayStatuses = [...new Set([...DEFAULT_JOB_STATUSES, ...apiStatuses])].filter(
          (s) => s !== FALLBACK_KEY && s !== 'Unsucessful'
        );

        const pipelineData = await pipelineRes.json();
        this.pipelines = pipelineData.pipelines || [];
      } catch (err) {
        this.error = err instanceof Error ? err.message : 'Failed to load data';
      } finally {
        this.loading = false;
      }
    },
    async saveMapping(status, pipelineStageId, opportunityStatus) {
      const ssoToken = await this.getSSOToken();
      const response = await fetch('/status-mappings', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-sso-token': ssoToken,
        },
        body: JSON.stringify({
          jobStatus: status,
          pipelineStageId: pipelineStageId || undefined,
          opportunityStatus: opportunityStatus || undefined,
        }),
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to save mapping');
      }
      const data = await response.json();
      this.statusMappings = data.mappings || this.statusMappings;
    },
    async handleStageChange(status, stageId) {
      const current = this.getMapping(status);
      try {
        await this.saveMapping(status, stageId, current.opportunityStatus);
      } catch (err) {
        alert(err instanceof Error ? err.message : 'Failed to save');
        await this.loadData();
      }
    },
    async handleOppStatusChange(status, oppStatus) {
      const current = this.getMapping(status);
      try {
        await this.saveMapping(status, current.pipelineStageId, oppStatus);
      } catch (err) {
        alert(err instanceof Error ? err.message : 'Failed to save');
        await this.loadData();
      }
    },
    async getSSOToken() {
      return await new Promise((resolve) => {
        window.parent.postMessage({ message: 'REQUEST_USER_DATA' }, '*');
        window.addEventListener('message', ({ data }) => {
          if (data.message === 'REQUEST_USER_DATA_RESPONSE') {
            resolve(data.payload);
          }
        });
      });
    },
  },
};
</script>

<style scoped>
.status-mapping-tab {
  width: 100%;
  padding: 20px;
}

.status-mapping-container {
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  padding: 24px;
}

.section-header {
  margin-bottom: 24px;
}

.section-header h3 {
  margin: 0 0 8px 0;
  font-size: 20px;
  font-weight: 600;
  color: #333;
}

.section-description {
  margin: 0;
  color: #666;
  line-height: 1.5;
}

.loading-state,
.error-state {
  padding: 40px;
  text-align: center;
  color: #666;
}

.btn-retry,
.btn-refresh {
  padding: 8px 16px;
  background: #24ACFE;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
}

.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  flex-wrap: wrap;
  gap: 12px;
}

.table-header h4 {
  margin: 0;
  font-size: 16px;
}

.status-count {
  margin-left: 8px;
  color: #888;
  font-size: 14px;
}

.mapping-table {
  width: 100%;
  border-collapse: collapse;
}

.mapping-table th,
.mapping-table td {
  padding: 12px 16px;
  text-align: left;
  border-bottom: 1px solid #eee;
}

.mapping-table th {
  background: #f8f9fa;
  font-weight: 600;
  color: #333;
  font-size: 13px;
}

.status-name {
  font-weight: 500;
  color: #333;
}

.sync-arrow {
  font-size: 18px;
  color: #24ACFE;
}

.pipeline-select,
.status-select {
  width: 100%;
  min-width: 180px;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  background: white;
}

.help-note {
  margin-top: 16px;
  font-size: 13px;
  color: #888;
}

.fallback-card {
  margin-bottom: 24px;
  padding: 16px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #f8fafc;
}

.fallback-card h4 {
  margin: 0 0 6px;
  font-size: 16px;
}

.fallback-copy {
  margin: 0 0 12px;
  color: #666;
  font-size: 13px;
  line-height: 1.4;
}

.fallback-row {
  display: grid;
  grid-template-columns: auto 1fr auto 1fr;
  gap: 10px 12px;
  align-items: center;
}

.fallback-row label {
  font-size: 13px;
  font-weight: 600;
  color: #333;
}
</style>
